import { MailOutlined } from "@ant-design/icons";
import { Alert, Button, Tooltip } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../lib/insuranceSaleApi";
import { useUser } from "../../../../../contexts/UserContext/UserContext";
import { isSolaGateway } from "../../../../../lib/sola/solaPaymentApi";

const RESPONSIVE_BTN_CLASS =
    "!inline-flex !h-auto !min-h-11 w-full !items-center !justify-center !rounded-xl !px-4 !py-2.5 !font-medium";

const InsuranceSalePaymentFields = ({
    amountDue,
    showAmountDue,
    showStripeButton,
    stripeLoading,
    onStripePay,
    showSendLinkButton,
    sendLinkLoading,
    sendLinkDisabled,
    sendLinkDisabledReason,
    onSendLink,
}) => {
    const { t } = useTranslation(["sellInsurance", "reservationPayment"]);
    const { myCompanyData } = useUser();
    const totalDue = Number(amountDue ?? 0);
    const sendLinkButtonDisabled = sendLinkDisabled || stripeLoading;

    const sendLinkButton = (
        <Button
            loading={sendLinkLoading}
            disabled={sendLinkButtonDisabled}
            onClick={onSendLink}
            block
            className={RESPONSIVE_BTN_CLASS}>
            <span className="flex items-center justify-center gap-2 text-center leading-snug">
                {!sendLinkLoading ? <MailOutlined className="shrink-0 text-base" /> : null}
                <span>
                    {sendLinkLoading ? (
                        t("pay.sending")
                    ) : (
                        <>
                            <span className="lg:hidden">{t("pay.sendPaymentLinkShort")}</span>
                            <span className="hidden lg:inline">{t("pay.sendPaymentLink")}</span>
                        </>
                    )}
                </span>
            </span>
        </Button>
    );

    const stripeLabel = isSolaGateway(myCompanyData)
        ? t("reservationPayment:card.title")
        : t("pay.payWithStripe");

    return (
        <div className="min-w-0 space-y-3 sm:space-y-4">
            {showAmountDue ? (
                <Alert
                    type="info"
                    showIcon
                    className="[&_.ant-alert-message]:break-words [&_.ant-alert-message]:text-sm"
                    message={t("pay.amountDue", { amount: formatCurrency(totalDue) })}
                />
            ) : null}

            {showStripeButton ? (
                <Button
                    type="primary"
                    loading={stripeLoading}
                    disabled={sendLinkLoading}
                    onClick={onStripePay}
                    block
                    className={RESPONSIVE_BTN_CLASS}>
                    <span className="text-center leading-snug">{stripeLabel}</span>
                </Button>
            ) : null}

            {showSendLinkButton ? (
                sendLinkDisabled && sendLinkDisabledReason ? (
                    <Tooltip title={sendLinkDisabledReason}>
                        <span className="block w-full">{sendLinkButton}</span>
                    </Tooltip>
                ) : (
                    sendLinkButton
                )
            ) : null}
        </div>
    );
};

InsuranceSalePaymentFields.propTypes = {
    amountDue: PropTypes.number.isRequired,
    showAmountDue: PropTypes.bool,
    showStripeButton: PropTypes.bool,
    stripeLoading: PropTypes.bool,
    onStripePay: PropTypes.func,
    showSendLinkButton: PropTypes.bool,
    sendLinkLoading: PropTypes.bool,
    sendLinkDisabled: PropTypes.bool,
    sendLinkDisabledReason: PropTypes.string,
    onSendLink: PropTypes.func,
};

InsuranceSalePaymentFields.defaultProps = {
    showAmountDue: true,
    showStripeButton: false,
    stripeLoading: false,
    onStripePay: undefined,
    showSendLinkButton: false,
    sendLinkLoading: false,
    sendLinkDisabled: false,
    sendLinkDisabledReason: undefined,
    onSendLink: undefined,
};

export default InsuranceSalePaymentFields;
